import React, { ChangeEvent } from 'react';

import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadIcon from '@mui/icons-material/Upload';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Button, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import {
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  ToolbarPropsOverrides,
} from '@mui/x-data-grid';
import { isDesktop } from 'react-device-detect';

import { useIntelState } from '../../../context/IntelState';
import { setEnemyData } from '../../../hooks/useEnemyData';
import { setOwnData } from '../../../hooks/useOwnData';
import { setRecentData } from '../../../hooks/useRecentIntel';

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    isViewingEnemy: boolean;
    handleFileUpload: (e: ChangeEvent<HTMLInputElement>) => void;
    isViewingRecent?: boolean;
  }
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const GridViewToolBarNew: React.FC<ToolbarPropsOverrides> = ({
  isViewingEnemy,
  handleFileUpload,
  isViewingRecent,
}) => {
  const { intelState, intelDispatch } = useIntelState();

  const refreshData = () => {
    if (isViewingRecent) {
      setRecentData(intelState.server, intelDispatch);
    } else if (isViewingEnemy) {
      setEnemyData(intelState.server, intelState.enemyKd, intelDispatch);
    } else {
      setOwnData(intelState.server, intelDispatch);
    }
  };

  const saveColumns = () => {
    const fileData = JSON.stringify(intelState.columnVisibility);
    const blob = new Blob([fileData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'utopiaColumns.json';
    link.href = url;
    link.click();
  };

  return (
    <Toolbar>
      <Stack direction='row' sx={{ gap: 0.5, flex: 1 }}>
        <ColumnsPanelTrigger
          render={
            <ToolbarButton
              render={
                <Button variant='text' startIcon={<ViewColumnIcon />}>
                  Columns
                </Button>
              }
            />
          }
        />
        {isDesktop && (
          <Tooltip title='Reset Columns'>
            <Button
              variant='text'
              size='small'
              startIcon={<DeleteSweepIcon />}
              onClick={() => intelDispatch({ type: 'RESET_COLS' })}
            >
              Reset
            </Button>
          </Tooltip>
        )}
        <FilterPanelTrigger
          render={
            <ToolbarButton
              render={
                <Button
                  variant='text'
                  size='small'
                  startIcon={<FilterListIcon />}
                >
                  Filters
                </Button>
              }
            />
          }
        />
        <Tooltip title='Export as CSV'>
          <ExportCsv
            render={
              <Button
                variant='text'
                size='small'
                startIcon={<FileDownloadIcon />}
              >
                CSV
              </Button>
            }
          />
        </Tooltip>
        {isDesktop && (
          <>
            <Button
              variant='text'
              size='small'
              startIcon={<FileDownloadIcon />}
              onClick={saveColumns}
            >
              Export Cols
            </Button>
            <Button
              component='label'
              variant='text'
              size='small'
              startIcon={<UploadIcon />}
            >
              Import Cols
              <VisuallyHiddenInput
                type='file'
                accept='application/json'
                onChange={(e) => {
                  handleFileUpload(e);
                  e.target.value = '';
                }}
              />
            </Button>
          </>
        )}

        <Tooltip title='Refresh'>
          <Button
            variant='text'
            size='small'
            startIcon={<RefreshIcon />}
            onClick={refreshData}
          >
            Refresh
          </Button>
        </Tooltip>
      </Stack>
    </Toolbar>
  );
};

export default GridViewToolBarNew;
